import React from 'react';
import { X } from 'lucide-react';

/**
 * ScheduleModal - Modal for scheduling messages/emails
 */
const ScheduleModal = ({
  isOpen,
  onClose,
  type = 'message', // 'message' or 'email'
  messagePreview,
  recipient,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
  scheduledMessages = [],
  onRemoveScheduled,
  onSchedule,
  className = ''
}) => {
  if (!isOpen) return null;

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`bg-black border border-gray-400 rounded-xl w-full max-w-md mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 ${className}`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">
            Schedule {type === 'message' ? 'Message' : 'Email'}
          </h3>
          <button 
            onClick={onClose}
            data-testid="schedule-modal-close"
            className="p-2 hover:bg-gray-900 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Message Preview */}
          {messagePreview && (
            <div className="p-3 bg-gray-800 rounded-lg border border-gray-600">
              <p className="text-xs text-gray-400 mb-1">Message to schedule:</p>
              <p className="text-sm text-white">
                {messagePreview.substring(0, 100)}
                {messagePreview.length > 100 ? '...' : ''}
              </p>
            </div>
          )}
          
          {/* Recipient */}
          {recipient && (
            <div className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg">
              {recipient.avatar && (
                <img src={recipient.avatar} alt="" className="w-8 h-8 rounded-full" />
              )}
              <div>
                <p className="text-sm text-white font-medium">{recipient.name}</p>
                <p className="text-xs text-gray-400">{recipient.phone || recipient.email}</p>
              </div>
            </div>
          )}

          {/* Date Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              min={today}
              data-testid="schedule-date-input"
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>

          {/* Time Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Time</label>
            <input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              data-testid="schedule-time-input"
              className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          
          {/* Scheduled Messages List */}
          {scheduledMessages.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-gray-400 mb-2">
                Scheduled Messages ({scheduledMessages.length})
              </p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {scheduledMessages.map((sm, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 bg-gray-800/50 rounded text-xs"
                  >
                    <div>
                      <p className="text-white">{sm.to}</p>
                      <p className="text-gray-400">
                        {new Date(sm.scheduledFor).toLocaleString()}
                      </p>
                    </div>
                    {onRemoveScheduled && (
                      <button 
                        onClick={() => onRemoveScheduled(idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={onSchedule}
            disabled={!scheduleDate || !scheduleTime}
            data-testid="schedule-confirm-btn"
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
